import type { MembershipSummary, Organization, OrganizationSnapshot } from "./organization-types";

export type DerivedOrganizationState = {
  home: Organization[];
  customers: Organization[];
  customerParentMap: Record<string, string>;
  memberships: MembershipSummary[];
  currentOrg: Organization | null;
  /**
   * The organisation to write into a JWT that names none, or null. Only ever
   * set on the refresh path: see `mayAdopt`.
   */
  adopt: Organization | null;
  /** What the tenant claim held when `adopt` was decided, for the tripwire log. */
  adoptFrom: string;
};

type DeriveOptions = {
  /**
   * Whether this derivation may write a first tenant claim.
   *
   * False for the server-seeded snapshot, and that is the whole point: a seed
   * is a picture of state the server already holds, so letting it write would
   * turn an ordinary page load into a workspace change. Only the refresh path,
   * which reads this browser's live claim, is allowed to fill an empty one.
   */
  mayAdopt: boolean;
};

/**
 * Turns one snapshot into the state the provider holds. Pure, so the seeded
 * path (server payload passed straight in) and the refresh path (the same
 * payload over `/api/organizations`) cannot diverge.
 */
export function deriveOrganizationState(
  data: OrganizationSnapshot,
  currentOrgId: string | null | undefined,
  { mayAdopt }: DeriveOptions
): DerivedOrganizationState {
  const activeOrg = data.activeOrganization;

  const applyBillingDetails = (org: Organization): Organization => {
    if (!activeOrg || org.id !== activeOrg.id) {
      return org;
    }

    return {
      ...org,
      billing_plan_id: activeOrg.billing_plan_id ?? null,
      subscription_status: activeOrg.subscription_status ?? null,
      trial_plan_id: activeOrg.trial_plan_id ?? null,
      trial_ends_at: activeOrg.trial_ends_at ?? null,
      billing_relationship: activeOrg.billing_relationship ?? null,
      stripe_customer_id: activeOrg.stripe_customer_id ?? null,
      pending_plan_id: activeOrg.pending_plan_id ?? null,
      pending_plan_effective_at: activeOrg.pending_plan_effective_at ?? null,
      access_expires_at: activeOrg.access_expires_at ?? null,
      managed_service_tier: activeOrg.managed_service_tier ?? null,
      is_invoice_billed: activeOrg.is_invoice_billed ?? null,
      billing_interval: activeOrg.billing_interval ?? null,
      has_had_trial: activeOrg.has_had_trial ?? null,
      discount_percent: activeOrg.discount_percent ?? null,
      phone_number_count: activeOrg.phone_number_count ?? null,
      metadata: activeOrg.metadata ?? null,
      has_onboarded_locale: activeOrg.has_onboarded_locale ?? false,
      is_verbu: activeOrg.is_verbu ?? false,
      approved_partner: activeOrg.approved_partner ?? false,
      partner_model: activeOrg.partner_model ?? null,
    };
  };

  const home = (data.homeOrganizations || []).map(applyBillingDetails).filter(Boolean);
  const customers = (data.customerOrganizations || []).map(applyBillingDetails).filter(Boolean);

  // Set current org from JWT claims or first available
  const combined = [...home, ...customers];
  const uniqueCombined = combined.filter((org, index, array) => org && array.findIndex((item) => item.id === org.id) === index);
  let current = uniqueCombined.find((org) => org.id === currentOrgId);

  // The org named by the JWT is authoritative even when it is in neither
  // list. Verbu staff opening a partner's customer land exactly here: the
  // switch is allowed, but the workspace is not a membership of theirs and
  // not a child of one, so it cannot appear in either list.
  //
  // activeOrganization is the SERVER's read of the current tenant, so it
  // is the more current of the two. Deliberately no equality check against
  // currentOrgId: that compares two independently-refreshing views of the
  // same claim (the server reads the cookie session, this reads the
  // client's in-memory one), and right after a switch they disagree. An
  // equality guard therefore fails exactly when it is needed most, which
  // is how staff were still being evicted from a customer workspace.
  if (!current && activeOrg) {
    current = activeOrg;
  }

  const activeOrgUnreadable = Boolean(data.activeOrganizationError);
  if (!current && activeOrgUnreadable) {
    console.warn("Active organization could not be loaded; keeping the current tenant claim.");
  }

  // Auto-assign an org ONLY for a user whose JWT TRULY names none.
  //
  // "Truly" means every view of the claim agrees it is empty: the server's, in
  // data.tenantId, and this browser's, in currentOrgId. The two refresh
  // independently, so either can read empty for a moment while a real tenant
  // exists, and acting on one alone is how a page load became a silent
  // workspace change. Requiring both is what makes this safe.
  //
  // A named tenant whose row did not arrive is never a cue to pick a new one,
  // whatever the reason: activeOrgUnreadable covers a read that failed, and a
  // null activeOrg covers everything else. Rewriting the claim in either state
  // evicts a user from a workspace they legitimately hold.
  //
  // mayAdopt keeps this off the seeded path entirely, so the only code that can
  // change a workspace is code the person's own browser asked to run.
  let adopt: Organization | null = null;
  const jwtNamesNoOrg = !(data.tenantId || currentOrgId);
  if (mayAdopt && jwtNamesNoOrg && !(activeOrg || activeOrgUnreadable) && uniqueCombined.length > 0) {
    // Prefer agency orgs over customer orgs when falling back. Within one kind
    // the first entry wins, and that is the OLDEST membership: both readers of
    // organization_members order by joined_at ascending (the snapshot loader
    // and getUserOrganizations), so somebody who belongs to two agencies always
    // lands in the one they joined first rather than in whichever row Postgres
    // handed back.
    const preferredOrg = uniqueCombined.find((org) => org.organization_type === "agency");
    adopt = preferredOrg || uniqueCombined[0];
    current = adopt;
  }

  return {
    home,
    customers,
    customerParentMap: data.customerParentMap || {},
    memberships: data.memberships || [],
    currentOrg: current ? applyBillingDetails(current) : activeOrg || null,
    adopt,
    adoptFrom: currentOrgId ?? "ABSENT",
  };
}
